import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import {
  IndicatorByService,
  IndicatorSectionKey,
  indicatorSectionsInOrder,
  IndicatorWithUpdate,
  Service,
} from '../../../../ReportBuilder/Components/ImpactIndicator/utils';
import {
  IndicatorDetails,
  SectionIndicators,
} from '../../../../ReportBuilder/Components/IndicatorDetails/utils';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactIconButton from 'shared/design-system/components/TrueImpactIconButton';
import {
  faClose,
  faEye,
  faEyeSlash,
  faSpinnerThird,
} from '@fortawesome/pro-light-svg-icons';
import TrueImpactIconText from 'shared/design-system/components/TrueImpactIconText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import ImpactTableHeaders from './ImpactTableHeaders';
import ImpactTableStage, {
  HandleAddIndicatorData,
  HandleDeleteIndicatorData,
  ImpactIndicatorById,
} from './ImpactTableStage';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import { TrueImpactFile } from 'shared/design-system/components/TrueImpactFileUploadBox';
import { useClient } from 'shared/apollo';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { FeedbackCommentBodyWithIndex } from '../../../utils';
import { updateBulkAnswer } from '../../api';
import TrueImpactPageLoading from 'shared/design-system/components/TrueImpactPageLoading';
import TrueImpactCollapse from 'shared/design-system/components/TrueImpactCollapse';
import ImpactComments from './ImpactComments';
import {
  ErrorsByIndicatorId,
  ValidateModelErrors,
  validateModel,
  ModelSaveWarning,
  HandleUpdatePropertyData,
} from './modelErrors';
import { getModelBulkAnswers } from 'shared/providers/IndicatorProvider/utils';
import {
  ReportStage,
  ReportStatus,
} from 'donor/portal/pages/FeedbackDashboard/api';
import BeneficiaryModelUpdates from './BeneficiaryModelUpdates.tsx';
import { useIndicator } from 'shared/providers/IndicatorProvider';
import { useReport } from 'shared/providers/ReportProvider';
import { mergeKeyNames } from 'shared/utils/mergeClassNames';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import {
  getBulkCrossSectionForecastMatches,
  stripCommas,
  getForecast,
  ReachForecastMatch,
  GuessMeasurementResult,
  getBulkGuessMeasurements,
  IsHumanResult,
  getBulkIsHumanIndicators,
  ForecastResultOneResult,
  getBulkForecastResultOne,
} from './forecastValidationUtils';
import { sectionKeyToString } from '../../../../ReportBuilder/Components/IndicatorDetails/utils';
import LevelOneFeedbackModal, { IndicatorNote } from './LevelOneFeedbackModal';
import {
  bulkUpdateReportIndicators,
  BulkUpdateIndicatorInput,
} from '../../../../ReportBuilder/Components/IndicatorDetails/api';
import { useFeatureFlags } from 'shared/providers/FeatureFlags/useFeatureFlags';
import TrueImpactLevelFeedbackBox from 'shared/design-system/components/TrueImpactLevelFeedbackBox';
import {
  getReachSucceedMatches,
  REACH_SUCCEED_MATCH_MESSAGE,
  REACH_SUCCEED_MATCH_TITLE,
} from './reachSucceedMatchUtils';
import ReachSucceedMatchModal from './ReachSucceedMatchModal';
import { updateReportLevel1ReachSucceedMatch } from '../../../../ReportBuilder/Components/Review/api';
import { logError } from 'shared/utils/logError';

const ShowErrors: React.FC<{
  errors: ValidateModelErrors[];
  handleClose: () => unknown;
}> = ({ errors, handleClose }) => {
  if (!errors || errors.length === 0) return null;
  return (
    <TrueImpactText color="critical" className="validation-errors">
      <div>
        {errors.map((e, i) => (
          <div key={`${e.indicationId || e.sectionKey}-${i}`}>
            {e.errorMessage}
          </div>
        ))}
        <TrueImpactIconText
          icon={faEye}
          label="Hide all errors"
          onClick={handleClose}
        />
      </div>
    </TrueImpactText>
  );
};

const getImpactIndicatorById = (
  impactDetails: SectionIndicators,
  impactSummary: IndicatorByService,
) => {
  const result: ImpactIndicatorById = {};
  indicatorSectionsInOrder.forEach(s => {
    const summaryIndicators = impactSummary[s] || [];
    for (let index = 0; index < impactDetails[s].length; index++) {
      const indicator = impactDetails[s][index];
      const summaryIndex = summaryIndicators.findIndex(
        si => si.id === indicator.indicatorId,
      );
      const summaryIndicator =
        summaryIndex >= 0 ? summaryIndicators[summaryIndex] : undefined;

      result[indicator.indicatorId] = {
        indicator,
        index,
        sectionKey: s,
        impactSummaryIndex: summaryIndex,
        impactSummaryIndicator: summaryIndicator,
      };
    }
  });
  return result;
};

export interface ImpactTableProps {
  heading?: string;
  nextButtonText?: string;
  reportStage: ReportStage;
  reportStatus: ReportStatus;
  reportId: number;
  service: Service;
  programName: string;
  showErrorsInButtonWrapper?: boolean;
  impactDetails: SectionIndicators;
  allIndicators: IndicatorByService;
  impactSummary: IndicatorByService;
  feedback: FeedbackCommentBodyWithIndex[];
  impactDetailAnswerId: number | undefined;
  impactSummaryAnswerId: number | undefined;
  handleBack?: () => unknown;
  handleDone: () => unknown;
  handleClose?: () => unknown;
  reloadAnswers: () => unknown;
}

const StyledDiv = styled.div`
  .heading-page {
    text-align: center;
    max-width: 888px;
    margin-left: auto;
    margin-right: auto;
  }
  .close-button {
    position: fixed;
    top: 20px;
    right: 30px;
  }
  .view-feedback-button {
    margin-bottom: ${SPACERS.medium};
  }
  .impact-table {
    max-width: 1100px;
    margin-left: auto;
    margin-right: auto;
    border-radius: ${SPACERS.small};
    border: 1px solid ${COLORS.grayLight};
    background-color: ${COLORS.whiteBase};
    box-shadow: 0 4px 16px rgba(68, 87, 105, 0.08);
    overflow: hidden;
    .headers,
    .table-body,
    .table-row {
      display: grid;
      grid-template-columns: 178px 1fr 164px;
    }
  }
  .button-wrapper {
    display: flex;
    gap: ${SPACERS.medium};
    justify-content: center;
    flex-direction: column;
    margin-top: ${SPACERS.extraLarge};
    margin-bottom: ${SPACERS.medium};
    button {
      margin-left: 0 !important;
      margin-right: 0 !important;
    }
    .button-group {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: ${SPACERS.medium};
    }
    .warning {
      text-align: center;
      color: ${COLORS.warningDark};
      margin-bottom: ${SPACERS.tiny};
    }
  }
  .top-right-radius {
    border-top-right-radius: ${SPACERS.tiny};
  }
  .top-left-radius {
    border-top-left-radius: ${SPACERS.tiny};
  }
  .bottom-right-radius {
    border-bottom-right-radius: ${SPACERS.tiny};
  }
  .bottom-left-radius {
    border-bottom-left-radius: ${SPACERS.tiny};
  }
  .validation-errors {
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    margin-top: ${SPACERS.large};
  }
  .impact-model-save-button {
    min-width: 250px;
  }
`;

const ImpactTable: React.FC<ImpactTableProps> = ({
  heading = 'Please make updates to your Impact Model based on the feedback provided.',
  nextButtonText = 'Save & Continue',
  service,
  reportId,
  feedback,
  reportStage,
  programName,
  allIndicators: a,
  impactSummary: initialSummary,
  impactDetails: initialDetails,
  showErrorsInButtonWrapper = false,
  handleBack,
  handleDone,
  handleClose,
  reloadAnswers,
}) => {
  const divRef = React.useRef<HTMLDivElement>(null);
  const [errors, setErrors] = useState<ValidateModelErrors[]>([]);
  const client = useClient();
  const [editingCount, setEditingCount] = useState<number>(0);
  const [showEditWarning, setShowEditWarning] = useState<boolean>(false);
  const [viewFeedback, setViewFeedback] = useState<boolean>(false);
  const [requestState, makeRequest] = useGraphQLRequest();
  const [errorsByIndicator, setErrorsByIndicator] = useState<
    ErrorsByIndicatorId
  >({});
  const [warningsByIndicator, setWarningsByIndicator] = useState<
    ErrorsByIndicatorId
  >({});
  const [bulkWarningModal, setBulkWarningModal] = useState<{
    open: boolean;
    reachForecastMatch: ReachForecastMatch[];
    guessMeasurements: GuessMeasurementResult[];
    isHumanIndicators: IsHumanResult[];
    forecastResultOne: ForecastResultOneResult[];
    sourceSectionLabel: string;
    targetSectionLabel: string;
    isDone: boolean;
    isSaving: boolean;
    errorMessage: string;
  }>({
    open: false,
    reachForecastMatch: [],
    guessMeasurements: [],
    isHumanIndicators: [],
    forecastResultOne: [],
    sourceSectionLabel: '',
    targetSectionLabel: '',
    isDone: false,
    isSaving: false,
    errorMessage: '',
  });
  const {
    impactSummary,
    impactDetails,
    allIndicators,
    handleAddIndicator,
    handleDeleteSection,
    handleDeleteIndicator,
    handleUpdateDescription,
    setImpactDetails,
    loadReportIndicators,
  } = useIndicator();
  const {
    forecastField,
    finalForecastField,
    isInterimStage,
    isReportEndDatePassed,
    report,
    handleReloadReport,
  } = useReport();
  const levelOneFeedbackEnabled = useFeatureFlags('levelOneFeedback');
  const hideForecast = forecastField(impactDetails.succeed[0]).hide;
  const hideFinalForecast = finalForecastField(impactDetails.succeed[0]).hide;
  const impactIndicatorsById = useMemo(
    () => getImpactIndicatorById(impactDetails, impactSummary),
    [impactDetails, impactSummary],
  );
  const [showReachSucceedModal, setShowReachSucceedModal] = useState<boolean>(
    false,
  );
  const [savingReachSucceedNote, setSavingReachSucceedNote] = useState<boolean>(
    false,
  );
  const [reachSucceedNoteError, setReachSucceedNoteError] = useState<string>(
    '',
  );
  const [localReachSucceedNote, setLocalReachSucceedNote] = useState<string>(
    '',
  );
  const advanceAfterReachSucceedNoteRef = useRef<boolean>(false);
  const reachSucceedIsDoneRef = useRef<boolean>(false);
  const reachSucceedNote =
    report.data?.level1ReachSucceedMatch || localReachSucceedNote;

  const reachSucceedMatches = useMemo(
    () =>
      levelOneFeedbackEnabled
        ? getReachSucceedMatches({
            impactDetails,
            hideForecast,
            hideFinalForecast,
          })
        : [],
    [levelOneFeedbackEnabled, impactDetails, hideForecast, hideFinalForecast],
  );
  const needsReachSucceedExplanation =
    reachSucceedMatches.length > 0 && !reachSucceedNote;

  const handleEditChange = useCallback(
    (warning: ModelSaveWarning, isEditing: boolean) => {
      setEditingCount(prev => {
        const next = prev + (isEditing ? 1 : -1);
        return next < 0 ? 0 : next;
      });
      setWarningsByIndicator(prev => ({
        ...prev,
        [warning.indicationId]: {
          ...prev[warning.indicationId],
          [warning.propertyName]: isEditing,
        },
      }));
    },
    [setEditingCount, setWarningsByIndicator],
  );

  // TIPD-4008
  // Use the report-level "passed" flag from the provider as the single source
  // of truth so the forecast/finalForecast field shown, validated and saved all
  // stay consistent (e.g. an INITIAL report whose end date has already passed).
  const isPassed = useMemo(() => {
    // return isReportPassed || isAnyIndicatorHasFinalResults(impactDetails);
    return isReportEndDatePassed;
  }, [isReportEndDatePassed]);

  const handleHideErrors = useCallback(() => {
    setErrors([]);
  }, [setErrors]);

  useEffect(() => {
    loadReportIndicators(reportId, true);
  }, [reportId]);

  const handleAdd = useCallback(
    (
      sectionKey: IndicatorSectionKey,
      indicator: IndicatorWithUpdate,
      data: HandleAddIndicatorData,
    ) => {
      handleAddIndicator(sectionKey, indicator);
    },
    [handleAddIndicator],
  );

  const handleDelete = useCallback(
    (data: HandleDeleteIndicatorData) => {
      handleDeleteIndicator(data.sectionKey, data.indicatorId);
    },
    [handleDeleteIndicator],
  );

  const handleUpdateProperty = useCallback(
    ({
      type,
      name,
      value,
      sectionKey,
      indicatorId,
    }: HandleUpdatePropertyData) => {
      if (type === 'impactSummary' && typeof value === 'string') {
        handleUpdateDescription(indicatorId, sectionKey, value);
        return;
      }
      if (type === 'impactDetail') {
        // State-only merge. The persisting API call lives in the
        // useSaveDetailProperties hook used by the saving component.
        setImpactDetails(s => {
          const newState = { ...s };
          const index = newState[sectionKey].findIndex(
            i => i.indicatorId === indicatorId,
          );
          if (index < 0) return s;
          newState[sectionKey] = [...newState[sectionKey]];
          newState[sectionKey][index] = {
            ...newState[sectionKey][index],
            [name]: value,
          };
          return newState;
        });
      }
    },
    [handleUpdateDescription, setImpactDetails],
  );

  const handleViewFeedback = useCallback(() => {
    setViewFeedback(o => !o);
  }, [setViewFeedback]);

  const isLoading = false;

  const performSave = useCallback(
    (isDone: boolean) => {
      if (!client) return;
      const bulkAnswers = getModelBulkAnswers(
        impactSummary,
        impactDetails,
        reportStage,
      );
      const request = updateBulkAnswer(client, reportId, bulkAnswers.result);
      makeRequest(request);
      request.then(() => {
        isDone && handleDone();
        handleClose?.();
        reloadAnswers();
      });
    },
    [
      client,
      impactSummary,
      impactDetails,
      reportStage,
      reportId,
      makeRequest,
      handleDone,
      handleClose,
      reloadAnswers,
    ],
  );

  const checkForForecastMatches = useCallback((): ReachForecastMatch[] => {
    const reachIndicators = impactDetails.reach || [];
    const capacityDevelopmentIndicators =
      impactDetails.capacityDevelopment || [];

    if (
      reachIndicators.length === 0 ||
      capacityDevelopmentIndicators.length === 0
    ) {
      return [];
    }

    const reachWithValues = reachIndicators.map(indicator => ({
      indicator,
      forecastValue: stripCommas(getForecast(false, indicator)),
      finalForecastValue: stripCommas(getForecast(true, indicator)),
    }));

    return getBulkCrossSectionForecastMatches({
      sourceIndicators: reachWithValues,
      targetIndicators: capacityDevelopmentIndicators,
      hideForecast,
      hideFinalForecast,
    });
  }, [impactDetails, hideForecast, hideFinalForecast]);

  const continueAfterReachSucceedCheck = useCallback(
    (isDone: boolean) => {
      const reachForecastMatch = checkForForecastMatches();
      const guessMeasurements = getBulkGuessMeasurements(impactDetails);
      const isHumanIndicators = getBulkIsHumanIndicators(impactDetails);
      const forecastResultOne = getBulkForecastResultOne(impactDetails);

      if (
        levelOneFeedbackEnabled &&
        (reachForecastMatch.length > 0 ||
          guessMeasurements.length > 0 ||
          isHumanIndicators.length > 0 ||
          forecastResultOne.length > 0)
      ) {
        setBulkWarningModal({
          open: true,
          reachForecastMatch,
          guessMeasurements,
          isHumanIndicators,
          forecastResultOne,
          sourceSectionLabel: sectionKeyToString('reach'),
          targetSectionLabel: sectionKeyToString('capacityDevelopment'),
          isDone,
          isSaving: false,
          errorMessage: '',
        });
        return;
      }

      performSave(isDone);
    },
    [
      impactDetails,
      checkForForecastMatches,
      performSave,
      levelOneFeedbackEnabled,
    ],
  );

  const onDoneWithChange = useCallback(
    (isDone: boolean) => () => {
      if (!client) return;
      if (editingCount > 0) {
        setShowEditWarning(true);
        return;
      }
      setShowEditWarning(false);
      const validations = validateModel(impactSummary, impactDetails, isPassed);
      if (validations.errors.length !== 0) {
        setErrors(validations.errors);
        setErrorsByIndicator(validations.errorsByIndicator);
        if (divRef.current) {
          divRef.current?.lastElementChild?.scrollIntoView({
            behavior: 'smooth',
          });
        }
        return;
      }
      setErrors([]);
      setErrorsByIndicator({});

      if (needsReachSucceedExplanation) {
        advanceAfterReachSucceedNoteRef.current = true;
        reachSucceedIsDoneRef.current = isDone;
        setShowReachSucceedModal(true);
        return;
      }

      continueAfterReachSucceedCheck(isDone);
    },
    [
      impactDetails,
      impactSummary,
      isPassed,
      divRef,
      editingCount,
      client,
      needsReachSucceedExplanation,
      continueAfterReachSucceedCheck,
    ],
  );

  const handleExplainReachSucceed = useCallback(() => {
    advanceAfterReachSucceedNoteRef.current = false;
    setReachSucceedNoteError('');
    setShowReachSucceedModal(true);
  }, []);

  const handleCloseReachSucceedModal = useCallback(() => {
    advanceAfterReachSucceedNoteRef.current = false;
    setReachSucceedNoteError('');
    setShowReachSucceedModal(false);
  }, []);

  const handleConfirmReachSucceedNote = useCallback(
    async (note: string) => {
      if (!client) return;
      setSavingReachSucceedNote(true);
      setReachSucceedNoteError('');
      try {
        await updateReportLevel1ReachSucceedMatch(client, reportId, note);
        setLocalReachSucceedNote(note);
        setShowReachSucceedModal(false);
        handleReloadReport();
        if (advanceAfterReachSucceedNoteRef.current) {
          advanceAfterReachSucceedNoteRef.current = false;
          continueAfterReachSucceedCheck(reachSucceedIsDoneRef.current);
        }
      } catch (e) {
        logError('Failed to save reach/succeed explanation:', e);
        setReachSucceedNoteError(
          'We could not save your note. Please try again.',
        );
      } finally {
        setSavingReachSucceedNote(false);
      }
    },
    [client, reportId, handleReloadReport, continueAfterReachSucceedCheck],
  );

  const handleCloseBulkWarning = useCallback(() => {
    setBulkWarningModal(s => ({ ...s, open: false }));
  }, []);

  const handleConfirmBulkWarning = useCallback(
    async (notes: IndicatorNote[]) => {
      if (!client) return;

      setBulkWarningModal(s => ({ ...s, isSaving: true, errorMessage: '' }));

      const updates: BulkUpdateIndicatorInput[] = [];
      const updatesById: Record<number, number> = {};
      notes.forEach(({ indicatorId, field, note }) => {
        if (updatesById[indicatorId] === undefined) {
          updatesById[indicatorId] = updates.length;
          updates.push({ reportId, indicatorId });
        }
        const index = updatesById[indicatorId];
        updates[index][field] = note;
      });

      try {
        await bulkUpdateReportIndicators(client, updates);

        // Update local state for each modified indicator
        notes.forEach(({ indicatorId, field, note }) => {
          const indicatorInfo = impactIndicatorsById[indicatorId];
          if (!indicatorInfo) return;

          handleUpdateProperty({
            type: 'impactDetail',
            name: field,
            value: note,
            sectionKey: indicatorInfo.sectionKey,
            indicatorId,
            impactDetailIndex: indicatorInfo.index,
            impactSummaryIndex: indicatorInfo.impactSummaryIndex,
            impactSummaryIndicator: indicatorInfo.impactSummaryIndicator!,
            impactDetailIndicator: indicatorInfo.indicator,
          });
        });

        setBulkWarningModal(s => ({ ...s, open: false, isSaving: false }));
        performSave(bulkWarningModal.isDone);
      } catch (error) {
        console.error('Failed to save level1 notes:', error);
        setBulkWarningModal(s => ({
          ...s,
          isSaving: false,
          errorMessage:
            'Failed to save your notes. Please check your connection and try again.',
        }));
      }
    },
    [
      bulkWarningModal.isDone,
      client,
      reportId,
      impactIndicatorsById,
      handleUpdateProperty,
      performSave,
    ],
  );

  return (
    <StyledDiv ref={divRef}>
      <TrueImpactPageLoading show={isLoading} />
      {handleClose && (
        <TrueImpactIconButton
          icon={
            requestState.type === 'REQUEST_START' ? faSpinnerThird : faClose
          }
          spin={requestState.type === 'REQUEST_START'}
          label="Close"
          className="close-button"
          onClick={onDoneWithChange(false)}
        />
      )}
      <TrueImpactText variant="heading-page" className="heading-page">
        {heading}
      </TrueImpactText>
      {feedback.length > 0 && (
        <>
          <TrueImpactIconText
            icon={viewFeedback ? faEyeSlash : faEye}
            margin="auto"
            onClick={handleViewFeedback}
            className="view-feedback-button"
            label={viewFeedback ? 'Hide Feedback' : 'View All Feedback'}
          />
          <TrueImpactCollapse show={viewFeedback}>
            <ImpactComments feedback={feedback} />
          </TrueImpactCollapse>
        </>
      )}
      <BeneficiaryModelUpdates />
      <div className="impact-table">
        <ImpactTableHeaders className="headers" />
        {indicatorSectionsInOrder.map((s, i) => {
          if (s === 'catalyzed') return null;
          return (
            <ImpactTableStage
              key={mergeKeyNames(s, i)}
              sectionKey={s}
              className="table-row"
              reportId={reportId}
              service={service}
              feedback={feedback}
              programName={programName}
              isReportPassed={isPassed}
              impactDetails={impactDetails}
              impactSummary={impactSummary}
              allIndicators={allIndicators}
              errorsByIndicator={errorsByIndicator}
              warningsByIndicator={showEditWarning ? warningsByIndicator : {}}
              impactIndicatorsById={impactIndicatorsById}
              handleAddIndicator={handleAdd}
              handleDeleteIndicator={handleDelete}
              handleDeleteSection={handleDeleteSection}
              handleUpdateProperty={handleUpdateProperty}
              onEditChange={handleEditChange}
            />
          );
        })}
      </div>
      {reachSucceedMatches.length > 0 && (
        <TrueImpactLevelFeedbackBox
          severity="warning"
          title={REACH_SUCCEED_MATCH_TITLE}
          message={REACH_SUCCEED_MATCH_MESSAGE}
          note={reachSucceedNote}
          onExplain={handleExplainReachSucceed}
        />
      )}
      {!showErrorsInButtonWrapper && (
        <ShowErrors errors={errors} handleClose={handleHideErrors} />
      )}
      <div className="button-wrapper">
        {showEditWarning && (
          <TrueImpactText className="warning">
            <FontAwesomeIcon icon={faTriangleExclamation} /> Please make sure
            all the fields are saved before leaving this page.
          </TrueImpactText>
        )}
        {showErrorsInButtonWrapper && (
          <ShowErrors errors={errors} handleClose={handleHideErrors} />
        )}
        <div className="button-group">
          {/* TIPD-4094 {handleBack && (
            <TrueImpactButton variant="secondary-large" onClick={handleBack}>
              Back
            </TrueImpactButton>
          )} */}
          <TrueImpactButton
            className="impact-model-save-button"
            isLoading={requestState.type === 'REQUEST_START'}
            onClick={onDoneWithChange(true)}
          >
            {nextButtonText}
          </TrueImpactButton>
        </div>
      </div>
      <ReachSucceedMatchModal
        open={showReachSucceedModal}
        matches={reachSucceedMatches}
        initialNote={reachSucceedNote}
        isInterimStage={isInterimStage}
        isSaving={savingReachSucceedNote}
        errorMessage={reachSucceedNoteError}
        onClose={handleCloseReachSucceedModal}
        onConfirm={handleConfirmReachSucceedNote}
      />
      <LevelOneFeedbackModal
        open={bulkWarningModal.open}
        onClose={handleCloseBulkWarning}
        onConfirm={handleConfirmBulkWarning}
        reachForecastMatch={bulkWarningModal.reachForecastMatch}
        guessMeasurements={bulkWarningModal.guessMeasurements}
        isHumanIndicators={bulkWarningModal.isHumanIndicators}
        forecastResultOne={bulkWarningModal.forecastResultOne}
        sourceSectionLabel={bulkWarningModal.sourceSectionLabel}
        targetSectionLabel={bulkWarningModal.targetSectionLabel}
        isInterimStage={isInterimStage}
        isSaving={bulkWarningModal.isSaving}
        errorMessage={bulkWarningModal.errorMessage}
      />
    </StyledDiv>
  );
};

export default ImpactTable;
